import { Injectable, inject, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

/**
 * HTTP Interceptor to automatically add JWT token to all API requests
 * and refresh expired access tokens using the refresh token.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private injector = inject(Injector);

  private get auth(): AuthService {
    return this.injector.get(AuthService);
  }

  private get router(): Router {
    return this.injector.get(Router);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isAuthEndpoint(req.url)) {
      return next.handle(req);
    }

    const token = this.auth.getAccessToken();
    const shouldRefresh = token !== null && this.auth.isTokenExpired(token) && this.auth.getRefreshToken() !== null;

    const handleRequest = (accessToken: string | null) => {
      const authReq = accessToken ? req.clone({
        setHeaders: { Authorization: `Bearer ${accessToken}` },
      }) : req;
      return next.handle(authReq);
    };

    if (shouldRefresh) {
      return from(this.auth.refreshAccessToken()).pipe(
        switchMap((success) => {
          if (success) {
            return handleRequest(this.auth.getAccessToken());
          }
          return throwError(() => new Error('Unable to refresh access token'));
        }),
        catchError((error) => {
          this.auth.logout();
          this.router.navigate(['/login']);
          return throwError(() => error);
        }),
      );
    }

    return handleRequest(token).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401(req, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.auth.getRefreshToken() || this.isAuthEndpoint(req.url)) {
      this.auth.logout();
      this.router.navigate(['/login']);
      return throwError(() => new Error('Unauthorized'));
    }

    return from(this.auth.refreshAccessToken()).pipe(
      switchMap((success) => {
        if (success) {
          const newToken = this.auth.getAccessToken();
          const authReq = newToken ? req.clone({
            setHeaders: { Authorization: `Bearer ${newToken}` },
          }) : req;
          return next.handle(authReq);
        }
        this.auth.logout();
        this.router.navigate(['/login']);
        return throwError(() => new Error('Unable to refresh access token'));
      }),
      catchError((error) => {
        this.auth.logout();
        this.router.navigate(['/login']);
        return throwError(() => error);
      }),
    );
  }

  private isAuthEndpoint(url: string): boolean {
    return url.includes('/api/auth/login') || url.includes('/api/auth/refresh');
  }
}

